import { useSnackbar } from 'baseui/snackbar';
import { useState } from 'react';
import { Heading } from 'react-aria-components';
import { Edit3, Plus, Trash, Trash2 } from 'react-feather';
import { Outlet, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { Pagination } from 'components/Pagination';
import { QueryResolver } from 'components/QueryResolver';
import { createTableBuilder } from 'components/Table';
import { Button } from 'components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from 'components/ui/card';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from 'components/ui/dialog';
import { Text } from 'components/ui/text';
import { DashboardContentHeader } from 'features/DashboardContentHeader';
import {
  useChecklistItemsQuery,
  useChecklistQuery,
  useChecklistsQuery,
  useCreateChecklistItemMutation,
  useDeleteChecklistItemMutation,
  useUpdateChecklistItemMutation,
} from 'services/api/checklists/endpoints';
import type { Checklist, ChecklistItem } from 'services/api/checklists/types';
import { isMutationSuccess } from 'services/api/base-api';
import { useLocale } from 'utils/hooks/useLocale';

export default function ChecklistsIndex() {
  const { t } = useLocale();
  return (
    <>
      <Outlet />
      <div className="flex-1 space-y-5">
        <DashboardContentHeader
          title={t('Checklists')}
          description={t('Manage reusable checklist templates for work orders')}
        />
        <section className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <aside className="shrink-0 lg:sticky lg:top-0 lg:w-96">
            <ChecklistDetails />
          </aside>
          <ChecklistsTable />
        </section>
      </div>
    </>
  );
}

function ChecklistDetails() {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const checklistId = z.coerce
    .number()
    .catch(0)
    .parse(searchParams.get('checklistId'));

  const query = useChecklistQuery(checklistId, { skip: checklistId === 0 });

  if (!checklistId) {
    return (
      <Card>
        <CardContent>
          <div className="flex min-h-[10rem] flex-col items-center justify-center text-center">
            <Text className="font-semibold">{t('No Checklist Selected')}</Text>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <Heading className="text-lg/7 font-semibold tracking-[-0.015em] text-zinc-950 dark:text-white sm:text-base/7">
          {t('Checklist Details')}
        </Heading>
      </CardHeader>
      <CardContent>
        <QueryResolver query={query}>
          {(checklist) => (
            <div className="space-y-4">
              <div>
                <h2 className="text-base font-semibold leading-6 text-gray-900">
                  {checklist.name}
                </h2>
                {checklist.description && (
                  <p className="mt-1 text-sm text-gray-500">
                    {checklist.description}
                  </p>
                )}
              </div>
              <ChecklistItemsPanel checklist={checklist} />
            </div>
          )}
        </QueryResolver>
      </CardContent>
      <CardFooter className="flex justify-end space-x-4">
        <Button
          href={{
            pathname: `${checklistId}/delete`,
            search: `?${searchParams.toString()}`,
          }}
          variant="plain"
          startEnhancer={<Trash className="h-4 w-4" />}
        >
          {t('Delete')}
        </Button>
        <Button
          href={{
            pathname: `${checklistId}/edit`,
            search: `?${searchParams.toString()}`,
          }}
          startEnhancer={<Edit3 className="h-4 w-4" />}
        >
          {t('Edit')}
        </Button>
      </CardFooter>
    </Card>
  );
}

function ChecklistItemsPanel({ checklist }: { checklist: Checklist }) {
  const { t } = useLocale();
  const snackbar = useSnackbar();
  const query = useChecklistItemsQuery(checklist.id);
  const [createItem, createMutation] = useCreateChecklistItemMutation();
  const [updateItem, updateMutation] = useUpdateChecklistItemMutation();
  const [deleteItem] = useDeleteChecklistItemMutation();

  const [addOpen, setAddOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);
  const [deletingItemId, setDeletingItemId] = useState<number | null>(null);

  const [addForm, setAddForm] = useState({
    name: '',
    description: '',
    sortOrder: '',
  });
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    sortOrder: '',
  });

  const handleAdd = async () => {
    if (!addForm.name.trim()) return;
    const result = await createItem({
      checklistId: checklist.id,
      name: addForm.name,
      description: addForm.description || null,
      sortOrder: addForm.sortOrder ? Number(addForm.sortOrder) : null,
    });
    if (isMutationSuccess(result)) {
      snackbar.enqueue({ message: t('Item added') });
      setAddOpen(false);
      setAddForm({ name: '', description: '', sortOrder: '' });
    }
  };

  const handleEdit = async () => {
    if (!editingItem || !editForm.name.trim()) return;
    const result = await updateItem({
      id: editingItem.id,
      checklistId: checklist.id,
      name: editForm.name,
      description: editForm.description || null,
      sortOrder: editForm.sortOrder ? Number(editForm.sortOrder) : null,
    });
    if (isMutationSuccess(result)) {
      snackbar.enqueue({ message: t('Item updated') });
      setEditingItem(null);
    }
  };

  const handleDelete = async (itemId: number) => {
    const result = await deleteItem({ id: itemId, checklistId: checklist.id });
    if (isMutationSuccess(result)) {
      snackbar.enqueue({ message: t('Item deleted') });
    }
    setDeletingItemId(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">{t('Items')}</h3>
        <Button
          startEnhancer={<Plus className="h-3 w-3" />}
          onClick={() => setAddOpen(true)}
        >
          {t('Add Item')}
        </Button>
      </div>

      <QueryResolver query={query}>
        {(items) =>
          items.length === 0 ? (
            <p className="text-sm text-gray-400 italic">
              {t('No items yet — add your first checklist item')}
            </p>
          ) : (
            <ul className="space-y-2">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="flex items-start justify-between rounded-md border border-gray-100 bg-gray-50 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {item.sortOrder != null && (
                        <span className="mr-1 text-gray-400">
                          {item.sortOrder}.
                        </span>
                      )}
                      {item.name}
                    </p>
                    {item.description && (
                      <p className="text-xs text-gray-500">{item.description}</p>
                    )}
                  </div>
                  <div className="ml-2 flex shrink-0 space-x-1">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600"
                      onClick={() => {
                        setEditingItem(item);
                        setEditForm({
                          name: item.name,
                          description: item.description ?? '',
                          sortOrder: item.sortOrder?.toString() ?? '',
                        });
                      }}
                      title={t('Edit')}
                    >
                      <Edit3 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-red-500"
                      onClick={() => setDeletingItemId(item.id)}
                      title={t('Delete')}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )
        }
      </QueryResolver>

      {/* Add Item Dialog */}
      <Dialog
        isOpen={addOpen}
        onOpenChange={(open) => !open && setAddOpen(false)}
        size="md"
      >
        <DialogTitle>{t('Add Checklist Item')}</DialogTitle>
        <DialogBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('Name')} <span className="text-red-500">*</span>
              </label>
              <input
                value={addForm.name}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, name: e.target.value }))
                }
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
                placeholder={t('Item name')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('Description')}
              </label>
              <textarea
                value={addForm.description}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, description: e.target.value }))
                }
                rows={2}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
                placeholder={t('Optional description')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('Sort Order')}
              </label>
              <input
                type="number"
                value={addForm.sortOrder}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, sortOrder: e.target.value }))
                }
                className="mt-1 block w-32 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
                placeholder="1"
              />
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button
            variant="plain"
            onPress={() => {
              setAddOpen(false);
              setAddForm({ name: '', description: '', sortOrder: '' });
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            onPress={handleAdd}
            isLoading={createMutation.isLoading}
            isDisabled={!addForm.name.trim()}
          >
            {t('Add Item')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog
        isOpen={editingItem !== null}
        onOpenChange={(open) => !open && setEditingItem(null)}
        size="md"
      >
        <DialogTitle>{t('Edit Checklist Item')}</DialogTitle>
        <DialogBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('Name')} <span className="text-red-500">*</span>
              </label>
              <input
                value={editForm.name}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, name: e.target.value }))
                }
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('Description')}
              </label>
              <textarea
                value={editForm.description}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, description: e.target.value }))
                }
                rows={2}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('Sort Order')}
              </label>
              <input
                type="number"
                value={editForm.sortOrder}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, sortOrder: e.target.value }))
                }
                className="mt-1 block w-32 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
              />
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button variant="plain" onPress={() => setEditingItem(null)}>
            {t('Cancel')}
          </Button>
          <Button
            onPress={handleEdit}
            isLoading={updateMutation.isLoading}
            isDisabled={!editForm.name.trim()}
          >
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Item Confirmation */}
      <Dialog
        isOpen={deletingItemId !== null}
        onOpenChange={(open) => !open && setDeletingItemId(null)}
        size="sm"
      >
        <DialogTitle>{t('Delete Item')}</DialogTitle>
        <DialogBody>
          <p className="text-sm text-gray-600">
            {t('Are you sure you want to delete this checklist item?')}
          </p>
        </DialogBody>
        <DialogActions>
          <Button variant="plain" onPress={() => setDeletingItemId(null)}>
            {t('Cancel')}
          </Button>
          <Button
            color="red"
            onPress={() =>
              deletingItemId !== null && handleDelete(deletingItemId)
            }
          >
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

const { Table, Column } = createTableBuilder<Checklist>();

function ChecklistsTable() {
  const { t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useChecklistsQuery();
  const checklists = query.data ?? [];

  const updateChecklistId = (id: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('checklistId', id.toString());
    return next;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
          <CardTitle>{t('Checklists')}</CardTitle>
          <div className="flex items-center justify-end divide-x lg:justify-start">
            <Button
              href={{ pathname: 'new', search: `?${searchParams.toString()}` }}
              startEnhancer={<Plus className="h-full w-full" />}
            >
              {t('Add Checklist')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table
          data={checklists}
          isLoading={query.isLoading || query.isFetching}
          isError={query.isError}
          isEmpty={checklists.length === 0}
        >
          <Column header={t('ID#')}>{(row) => row.id}</Column>
          <Column header={t('Name')}>
            {(row) => (
              <span className="inline-block max-w-[14rem] truncate">
                {row.name}
              </span>
            )}
          </Column>
          <Column header={t('Description')}>
            {(row) => (
              <span className="inline-block max-w-[14rem] truncate text-gray-500">
                {row.description ?? '—'}
              </span>
            )}
          </Column>
          <Column header={t('Action')}>
            {(row) => (
              <Button
                href={{
                  pathname: window.location.pathname,
                  search: `?${updateChecklistId(row.id).toString()}`,
                }}
                variant="plain"
              >
                {t('View')}
              </Button>
            )}
          </Column>
        </Table>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Pagination
          pageSize={checklists.length}
          totalItems={checklists.length}
        />
      </CardFooter>
    </Card>
  );
}
